import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBoardLabelCatalog } from '@/entities/collab';
import { type Database, useSetDatabaseLabels } from '@/entities/database';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions, LoadingState, MultiSelect } from '@/shared/ui';

import styles from './DatabaseCatalog.module.css';

/**
 * The labels shown on a database's CARD: chosen from the workspace's own label catalog (the one a
 * board's labels come from). A marker only - it never gives or takes access. The catalog is
 * managed in the workspace; this dialog only picks.
 */
export function DatabaseLabelsDialog({
  database,
  onClose,
}: {
  database: Database;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const catalog = useBoardLabelCatalog(database.workspace_id);
  const save = useSetDatabaseLabels(database.id);
  const [chosen, setChosen] = useState<string[]>(database.labels.map((label) => label.id));
  const options = (catalog.data ?? []).map((label) => ({
    value: label.id,
    label: label.name,
    color: label.color,
  }));
  return (
    <Dialog title={t('databases.labels.title', { name: database.name })} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(chosen, { onSuccess: onClose });
        }}
      >
        {catalog.isPending ? (
          <LoadingState />
        ) : options.length === 0 ? (
          <p className={styles.hint}>{t('databases.labels.empty')}</p>
        ) : (
          <MultiSelect
            label={t('databases.labels.field')}
            options={options}
            value={chosen}
            onChange={setChosen}
            searchPlaceholder={t('common.search')}
            emptyText={t('common.noResults')}
          />
        )}
        <p className={styles.hint}>{t('databases.labels.hint')}</p>
        <FormError error={save.error ?? catalog.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending || catalog.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
